import * as Ariakit from '@ariakit/react';
import classNames from 'classnames';
import { IconCheck, IconCheckSquare, IconChevronDown, IconChevronRight, IconSquare } from 'featherico';
import type React from 'react';
import { createContext, use } from 'react';
import { useBooleanState } from '../../hooks/useToggle';
import { trackEvent, type EventTrackingProps } from '../../services/Tracking';
import Button, { type ButtonKind } from '../Button';
import Link from '../Link';
import { ConfirmModal, type ConfirmModalProps } from '../Modal/ConfirmModal';
import styles from './style.m.less';

export type MenuPlacement = Ariakit.MenuProviderProps['placement'];

type DropdownTriggerProps =
    | { label: React.ReactNode; isSmall?: boolean; kind?: ButtonKind; trigger?: never }
    | { label?: never; isSmall?: never; kind?: never; trigger: Ariakit.MenuButtonProps['render'] };

type DropdownMenuProps = {
    children: React.ReactNode;
    placement?: MenuPlacement;
    triggerWidthAligned?: boolean;
    eventTracking?: EventTrackingProps;
} & DropdownTriggerProps;

export const DropdownMenu = ({
    label,
    isSmall = true,
    kind,
    trigger,
    children,
    placement = 'bottom-end',
    triggerWidthAligned,
    eventTracking,
}: DropdownMenuProps) => {
    return (
        <Ariakit.MenuProvider
            placement={placement}
            setOpen={eventTracking ? open => open && trackEvent(eventTracking) : undefined}
        >
            <Ariakit.MenuButton
                render={
                    trigger ?? (
                        <Button isSmall={isSmall} kind={kind}>
                            {label} <IconChevronDown />
                        </Button>
                    )
                }
            />
            <Ariakit.Menu
                className={triggerWidthAligned ? styles.contentTriggerWidthAligned : styles.content}
                portal
                gutter={5}
                unmountOnHide
            >
                {children}
            </Ariakit.Menu>
        </Ariakit.MenuProvider>
    );
};

type SubmenuProps = {
    label: React.ReactNode;
    children: React.ReactNode;
    eventTracking?: EventTrackingProps;
};

export const Submenu = ({ label, children, eventTracking }: SubmenuProps) => {
    return (
        <Ariakit.MenuProvider
            placement="right-start"
            setOpen={eventTracking ? open => open && trackEvent(eventTracking) : undefined}
        >
            <Ariakit.MenuButton className={styles.submenuTrigger} render={<MenuItem />}>
                {label}{' '}
                <span>
                    <IconChevronRight className={styles.iconSubmenu} />
                </span>
            </Ariakit.MenuButton>
            <Ariakit.Menu className={styles.content} gutter={-5} shift={-6} unmountOnHide portal>
                {children}
            </Ariakit.Menu>
        </Ariakit.MenuProvider>
    );
};

// Extend as needed
type DefaultItemProps = 'children' | 'onClick' | 'disabled' | 'className' | 'aria-label' | 'aria-checked' | 'role';

type MenuItemProps = Pick<Ariakit.MenuItemProps, DefaultItemProps | 'hideOnClick'> & {
    eventTracking?: EventTrackingProps;
};

export const MenuItem = ({ eventTracking, ...props }: MenuItemProps) => {
    return (
        <Ariakit.MenuItem
            {...props}
            onClick={useWithEventTracking(props.onClick, eventTracking)}
            className={classNames(props.className, styles.item)}
        />
    );
};

type MenuLinkProps = MenuItemProps & Pick<React.ComponentProps<typeof Link>, 'href' | 'openInModal' | 'openInNewTab'>;

export const MenuLink = ({ href, openInModal, openInNewTab, eventTracking, ...props }: MenuLinkProps) => {
    return (
        <Ariakit.MenuItem
            {...props}
            onClick={useWithEventTracking(props.onClick, eventTracking)}
            className={classNames(props.className, styles.item)}
            render={<Link href={href} openInModal={openInModal} openInNewTab={openInNewTab} />}
        />
    );
};

type MenuItemWithConfirmProps = MenuItemProps & {
    onClick?: () => void;
    hideOnClick?: boolean;
    confirmProps?: Omit<ConfirmModalProps, 'isOpen' | 'onClose' | 'onConfirm'>;
};

export const MenuItemWithConfirm = ({
    confirmProps = {
        title: 'Are you sure?',
    },
    hideOnClick = true,
    onClick,
    ...props
}: MenuItemWithConfirmProps) => {
    'use memo';
    const menu = Ariakit.useMenuContext();
    const [isOpen, openModal, onClose] = useBooleanState(false);

    const onConfirm = () => {
        onClick?.();
        if (hideOnClick) {
            menu?.hideAll();
        }
    };

    return (
        <>
            <MenuItem {...props} hideOnClick={false} onClick={openModal} />
            <ConfirmModal {...confirmProps} isOpen={isOpen} onClose={onClose} onConfirm={onConfirm} />
        </>
    );
};

export const MenuSeparator = () => <Ariakit.MenuSeparator className={styles.separator} />;

type MenuRadioGroupProps<T extends string> = {
    value: T | undefined;
    onChange: (value: T) => void;
    children: React.ReactNode;
};

type RadioGroupContext = {
    value: string | undefined;
    onChange: (value: string) => void;
};

const radioGroupContext = createContext<RadioGroupContext>({ value: undefined, onChange: () => {} });

export function MenuRadioGroup<T extends string>({ value, onChange, children }: MenuRadioGroupProps<T>) {
    return (
        <radioGroupContext.Provider value={{ value, onChange: onChange as (value: string) => void }}>
            <Ariakit.MenuGroup>{children}</Ariakit.MenuGroup>
        </radioGroupContext.Provider>
    );
}

type MenuRadioProps<T extends string> = MenuItemProps & {
    value: T;
};

export function MenuRadio<T extends string>({
    value,
    role = 'menuitemradio',
    hideOnClick = true,
    children,
    ...props
}: MenuRadioProps<T>) {
    const group = use(radioGroupContext);
    return (
        <MenuItem
            {...props}
            role={role}
            aria-checked={group.value === value}
            hideOnClick={hideOnClick}
            className={classNames(props.className, styles.itemRadio)}
            onClick={event => {
                props.onClick?.(event);
                group.onChange(value);
            }}
        >
            <IconCheck className={styles.iconCheck} /> {children}
        </MenuItem>
    );
}

type MenuCheckboxProps = MenuItemProps & { checked: boolean; onChange?: (checked: boolean) => void };

export function MenuCheckbox({
    checked,
    onChange,
    role = 'menuitemcheckbox',
    hideOnClick = false,
    children,
    ...props
}: MenuCheckboxProps) {
    'use memo';
    return (
        <MenuItem
            {...props}
            role={role}
            aria-checked={checked}
            hideOnClick={hideOnClick}
            className={classNames(props.className, styles.itemCheckbox)}
            onClick={
                onChange
                    ? event => {
                          props.onClick?.(event);
                          onChange(!checked);
                      }
                    : props.onClick
            }
        >
            {checked ? (
                <IconCheckSquare className={styles.iconCheckbox} />
            ) : (
                <IconSquare className={styles.iconCheckboxUnchecked} />
            )}{' '}
            {children}
        </MenuItem>
    );
}

const useWithEventTracking = (
    originalOnClick?: React.MouseEventHandler,
    eventTracking?: EventTrackingProps
): React.MouseEventHandler | undefined => {
    'use memo';

    if (!eventTracking) {
        return originalOnClick;
    }

    return event => {
        originalOnClick?.(event);
        trackEvent(eventTracking);
    };
};
